import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PinDropIcon from '@material-ui/icons/PinDrop';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import PlaceAddressLinkDialog from './PlaceAddressLinkDialog';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import { getPlaceAddresses, deletePlaceAddressLink } from '../../services/addressService';
import { ADDRESS_GRID_COLUMNS } from '../../constants/placeAddressesTabConstants';
import FormSpinner from 'components/FormSpinner';

const PlaceAddressesTab = ({ ptsPlaceID }) => {
  const dispatch = useDispatch();
  const { openAddressDialog } = useAddressDialog();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  const canEditPlace = usePermissions(PERMISSION_RESOURCES.PLACE).Edit;
  const addressPerms = usePermissions(PERMISSION_RESOURCES.ADDRESS);
  const canViewAddressRecord = Boolean(addressPerms.Read || addressPerms.Edit);

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAddresses = useCallback(async () => {
    if (!ptsPlaceID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPlaceAddresses(ptsPlaceID),
      onSuccess: (data) => setAddresses(data),
    });
  }, [dispatch, ptsPlaceID]);

  const handleAdd = useCallback(() => {
    setEditingLink(null);
    setLinkFormOpen(true);
  }, []);

  const handleManagePrimary = useCallback((row) => {
    setEditingLink(row);
    setLinkFormOpen(true);
  }, []);

  const handleOpenAddressRecord = useCallback(
    (row) =>
      openAddressDialog(row.ptsAddressID, {
        closeOnSave: true,
        onAddressUpdated: loadAddresses,
        onAddressDeleted: loadAddresses,
      }),
    [openAddressDialog, loadAddresses]
  );

  const handleLinkFormClose = useCallback(() => {
    setLinkFormOpen(false);
    setEditingLink(null);
  }, []);

  const handleLinkSaved = useCallback(() => {
    handleLinkFormClose();
    loadAddresses();
  }, [handleLinkFormClose, loadAddresses]);

  const performDelete = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: () => deletePlaceAddressLink({ ptsPlaceID, ptsAddressID: row.ptsAddressID }),
        onSuccess: () => {
          dispatch(notify('Address removed successfully', 'success'));
          loadAddresses();
        },
      });
    },
    [dispatch, loadAddresses, ptsPlaceID]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${
          row.FullAddressText || 'address'
        }" from this place?`,
        () => performDelete(row),
        'Remove Address'
      );
    },
    [performDelete, requestConfirm]
  );

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  return (
    <Box className="person-names-tab__grid-wrap place-grid-tab">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Address"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPlace}
        />
      </Box>

      <CommonDataGrid
        rows={addresses}
        columns={ADDRESS_GRID_COLUMNS}
        getRowId={(row) => row.ptsAddressID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Place Addresses' })}
        actions={{
          custom: [
            {
              icon: <EditIcon />,
              tooltip: 'Manage Primary',
              onClick: handleManagePrimary,
              disabled: !canEditPlace,
            },
            canViewAddressRecord && {
              icon: <PinDropIcon />,
              tooltip: 'Open Address Record',
              onClick: handleOpenAddressRecord,
              disabled: !canEditPlace,
            },
          ].filter(Boolean),
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPlace },
        }}
      />

      {linkFormOpen && (
        <PlaceAddressLinkDialog
          open={linkFormOpen}
          ptsPlaceID={ptsPlaceID}
          addressLink={editingLink}
          onClose={handleLinkFormClose}
          onSaved={handleLinkSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PlaceAddressesTab;
